/* Customer-portal shared runtime — C tokens, icons, field wrapper, airport
 * typeahead, step indicator, and the top Nav.
 *
 * Ported verbatim from the legacy templates/ELCAPP.html (2026-08 esbuild
 * port). Deliberately does NOT import from src/common.js: that module is the
 * STAFF runtime (Sidebar/useMe hit /api/elcapp/admin/me, which 401s for
 * customers) and esbuild inlines whole imports per entry — pulling it in
 * would embed admin code in the customer bundle. The shared pieces live in
 * src/shared/ui.js (hooks + C) and src/newquote/* (quoting toolkit).
 *
 * React / ReactDOM are runtime globals provided by the vendored UMD scripts
 * the elcapp_portal.html shell loads before the bundle.
 */

// Hooks + colour tokens from the neutral shared module (2026-11-03) — the
// same C the staff runtime re-exports, and the only thing src/newquote/*
// needs, so the quoting toolkit now works in this bundle too.
import { useState, useEffect, useMemo, useRef, C } from '../shared/ui.js';

const Ic = {
  Plane: ({ sz = 18, r = 0, col = 'currentColor', st = {} }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill={col} style={{ transform: `rotate(${r}deg)`, ...st }}><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" /></svg>,
  ChevR: ({ sz = 13 }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>,
  Pin: ({ sz = 14 }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3" /><path d="M12 2a8 8 0 0 0-8 8c0 5.333 8 14 8 14s8-8.667 8-14a8 8 0 0 0-8-8z" /></svg>,
  Check: ({ sz = 14, col = 'currentColor' }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  Clock: ({ sz = 14 }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
  User: ({ sz = 14 }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  Refresh: ({ sz = 14 }) => <svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-4.36" /></svg>
};

const F = ({ label, children, style = {} }) => <div style={style}><span className="lbl">{label}</span>{children}</div>;

// UNLOCO search typeahead. `options` is the airport list from /api/elcapp/airports
// (each row has {code, locode, name, country}). `value` is the human-readable
// "<name> (<IATA>)" string the downstream code already parses with a `(XXX)`
// regex, so picking a row stays compatible with the existing quote flow.
const AirportSearch = ({ value, onChange, options, placeholder, leadingIcon = null }) => {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!focused) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setQ(''); setFocused(false); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [focused]);
  const filtered = q
    ? (options || []).filter((o) => {
        const lq = q.toLowerCase();
        return (o.locode || '').toLowerCase().includes(lq)
            || (o.name   || '').toLowerCase().includes(lq)
            || (o.code   || '').toLowerCase().includes(lq);
      }).slice(0, 50)
    : [];
  const pick = (o) => { onChange(`${o.name} (${o.locode})`); setQ(''); setFocused(false); };
  return <div ref={ref} style={{ position: 'relative' }}>
    {leadingIcon && <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.textD, pointerEvents: 'none', zIndex: 1 }}>{leadingIcon}</span>}
    <input
      value={focused ? q : value}
      placeholder={placeholder}
      onFocus={() => setFocused(true)}
      onChange={(e) => setQ(e.target.value)}
      style={{ paddingLeft: leadingIcon ? 32 : undefined }}
    />
    {focused && q && <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: `1px solid ${C.brd}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', maxHeight: 280, overflowY: 'auto', zIndex: 100 }}>
      {filtered.length === 0
        ? <div style={{ padding: '12px 14px', fontSize: 12, color: C.textD }}>No matches</div>
        : filtered.map((o) =>
          <div key={o.code} onMouseDown={() => pick(o)}
               style={{ padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}
               onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,99,235,0.06)'}
               onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
            <span className="mono" style={{ fontWeight: 700, color: C.blueL, minWidth: 42 }}>{o.locode}</span>
            <span style={{ flex: 1, color: C.text }}>{o.name}</span>
            <span style={{ fontSize: 11, color: C.textD }}>{o.country}</span>
          </div>
        )}
    </div>}
  </div>;
};

const Steps = ({ cur }) => {
  const steps = ['Get Quote', 'Review', 'Book', 'Confirm'];
  return <div style={{ display: 'flex', alignItems: 'center', marginBottom: 32 }}>
    {steps.map((s, i) => <React.Fragment key={i}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
          background: i < cur ? C.green : i === cur ? C.blue : C.s3,
          border: i > cur ? `1px solid ${C.brd}` : 'none', color: i <= cur ? '#fff' : C.textD,
          boxShadow: i === cur ? `0 0 14px ${C.blue}55` : undefined,
          animation: i === cur ? 'pulse 2s infinite' : undefined }}>
          {i < cur ? <Ic.Check sz={11} col="#fff" /> : i + 1}
        </div>
        <span style={{ fontSize: 12, color: i === cur ? C.text : i < cur ? C.textM : C.textD, fontWeight: i === cur ? 600 : 400 }}>{s}</span>
      </div>
      {i < 3 && <div style={{ flex: 1, height: 1, background: i < cur ? C.green : C.brd, margin: '0 10px' }} />}
    </React.Fragment>)}
  </div>;
};

const Nav = ({ screen, setScreen, user, onLogout }) =>
<nav style={{ position: 'sticky', top: 0, zIndex: 200, background: 'rgba(248,250,252,0.96)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.brd}`, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', height: 60, display: 'flex', alignItems: 'center', padding: '0 32px', gap: 0 }}>
    <div onClick={() => setScreen('landing')} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginRight: 36, flexShrink: 0 }}>
      <img src="/logos/ELC%20Logo%20Full.png" alt="Express Line Corporation" style={{ height: 38, width: 'auto', display: 'block' }} />
    </div>
    {/* 2026-08 fixes: the "Tracking" tab is gone — it rendered a hardcoded
        fictional booking to anonymous visitors (there is no tracking backend);
        the confirmation screen is still reached after a real booking. The
        "⚙ Admin" button is gone too — a customer-facing page shouldn't point
        at the staff console. */}
    {[['landing', 'Home'], ['quote', 'Get Quote'], ['myquotes', 'My Quotes']].map(([id, lb]) =>
  <button key={id} className="bgh" style={{ fontSize: 13, padding: '6px 14px', color: screen === id ? C.blue : C.textM, background: screen === id ? 'rgba(37,99,235,0.08)' : 'transparent', fontFamily: 'Barlow,sans-serif', fontWeight: screen === id ? 600 : 400, borderRadius: 6, border: 'none', cursor: 'pointer' }} onClick={() => setScreen(id)}>{lb}</button>
  )}
    <div style={{ flex: 1 }} />
    {user.loggedIn
      ? <>
          <span style={{ fontSize: 12, color: C.textM, marginRight: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>{user.company}</span>
          <button className="btn bo" onClick={onLogout} style={{ fontSize: 12, padding: '7px 16px', marginRight: 8 }}>Logout</button>
        </>
      : <button className="btn bo" onClick={() => setScreen('login')} style={{ fontSize: 12, padding: '7px 16px', marginRight: 8 }}>Login</button>
    }
    <button className="btn bb" onClick={() => setScreen('quote')} style={{ padding: '7px 18px', fontSize: 13 }}>Get Quote</button>
  </nav>;

export { useState, useEffect, useMemo, useRef, C, Ic, F, AirportSearch, Steps, Nav };
